import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createLead,
  submitAfterOtp,
  lookupLeadAndSendOtp,
  sendOtpMobile,
  updateLead,
  updateLeadUrlAttribution,
  verifyOtp,
} from "@/api/lead";
import { useToast } from "@/hooks/useToast";
import {
  setIsLeadSubmittedSuccessfully,
  setLeadDetailsAction,
} from "@/store/slices/globalslice";
import type { LeadDataProps } from "@/types/lead";
import { RootState } from "@/store/store";
import { getLeadEmailFieldError } from "@/lib/validation";
import { getEntryReferrer } from "@/utils/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

/** Payload shape for `send-otp-mobile` / `submit` registration merge */

/**
 * Attribution already resolved by the caller, under its destination's rules.
 *
 * Only the CONFIG-DRIVEN CUSTOM FORM passes this, from exactly two places that must agree —
 * `useLeadFormSession` (which MINTS the row via the non-verified capture) and
 * `ConfigLeadForm` (whose step submit ships the same values as `context`, where
 * `attributionBackfill` fills whatever the capture left as a placeholder).
 *
 * It arrives RESOLVED rather than as a mode string because the rules differ per drop
 * destination — `lsq` reads only `utm_*` and leaves "" when the URL carried none, `erp`
 * reads the bare `?source=/media=/campaign=` triplet and falls back to the university
 * source and "Direct" (see `server/lib/leadAttributionRules.js`). This builder cannot know
 * the destination, and a mode string that said "url" without saying WHICH rules is how the
 * two ends drifted before: it re-read the URL here under one set of assumptions while the
 * caller had already read it under another.
 */
export interface ResolvedLeadAttribution {
  source_source: string;
  source_media: string;
  source_campaign: string;
  source_term: string;
  /**
   * Whether the URL this was read from actually carried attribution parameters.
   *
   * Sent on so the SERVER can tell a real second campaign click apart from an organic
   * return. A returning visitor's stored tuple is replaced only when this is true.
   */
  from_url?: boolean;
}

export function buildMobileOtpLeadPayload(
  formValues: FormValues,
  university: { source?: string; uni_code?: string } | null | undefined,
  attribution: ResolvedLeadAttribution | null = null
): Record<string, unknown> {
  /*
   * ATTRIBUTION. The two resolutions sit side by side deliberately — apart, either one can
   * be edited in the belief that it is the only one.
   *
   * NO ARGUMENT = THE WIDGET BRANCH, and every lead-drop widget is on it: `source_media` /
   * `source_campaign` are whatever the widget itself put in `formValues` (its own bare
   * `?media=` / `?campaign=` reads, else the literal "Direct"), `source_source` is the
   * university setting, and no `source_term` is sent. The URL is never read here.
   *
   * Know what that costs before moving a form onto it: this payload is what MINTS the lead
   * row (the non-verified capture fires as soon as a valid phone is typed), so a lead from a
   * `utm_medium=cpc` click is stored as Direct, and since the vendor reads its `utm_*`
   * headers off these columns the drop reports Direct too — indistinguishable from organic
   * traffic. That is the DEG-9155 bug, kept on purpose for the widgets: it is the
   * pre-DEG-9155 behaviour, restored by request, and it is deliberately UNCHANGED by the
   * per-destination work below.
   *
   * `source_term` is omitted rather than sent blank on the widget branch, as it was before
   * that commit. Both are no-ops server-side: the create path coalesces
   * `data?.source_term || ""` and the capture-update path does not read the key at all.
   */
  const o: Record<string, unknown> = {
    mobile: String(formValues.phone || "").trim(),
    name: formValues.name || "",
    email: formValues.email || "",
    highest_qualification:
      formValues.qualification || formValues.highest_qualification || "",
    area_of_interest:
      formValues.course || formValues.interest || "",
    ...(attribution
      ? {
          /*
           * RESOLVED BY THE CALLER — used VERBATIM.
           *
           * No `|| "Direct"` and no `|| university?.source` here: those fallbacks ARE the
           * rules, and the rules already ran. `erp` put the university source and "Direct"
           * in these fields itself; `lsq` deliberately left "" because a stand-in there is
           * not a default but a fabricated campaign in LeadSquared's reporting. Re-applying
           * either default here would silently undo the `lsq` half of the rule.
           *
           * `formValues.media` / `.campaign` are IGNORED on this branch, on purpose. They
           * carry the same fact read a second time, and two readers for one fact is exactly
           * what made a `utm_medium=cpc` visit store as Direct: the widget-shaped "Direct"
           * sitting in `formValues` was TRUTHY and shadowed the real value behind it.
           */
          source_media: attribution.source_media,
          source_campaign: attribution.source_campaign,
          source_term: attribution.source_term,
          // The per-lead CRM override still wins — see FormValues above. Nothing else does.
          source_source: formValues.source_source || attribution.source_source,
          /*
           * "This visit arrived on a campaign link." Lets the server REPLACE a returning
           * lead's stored attribution instead of keeping the first visit's forever — while
           * an organic return (false) leaves the stored campaign untouched rather than
           * erasing it. See `hasUrlAttribution`.
           */
          attribution_from_url: attribution.from_url === true,
          /*
           * "These four are authoritative, including the empty ones."
           *
           * `createLead` coalesces `source_media || "Direct"` when it mints a row, which
           * would turn a correct `lsq` blank straight back into a fabricated "Direct". The
           * server honours explicit empties only when it sees this flag, so the widgets —
           * which never send it — keep that coercion untouched.
           */
          attribution_resolved: true,
        }
      : {
          source_media: formValues.media || "Direct",
          source_campaign: formValues.campaign || "Direct",
          // Caller override wins over the university default — see FormValues above.
          source_source: formValues.source_source || university?.source || "",
        }),
    source_refer_url:
      typeof window !== "undefined" ? window.location.href : "",
    uni_code: formValues.uni_code || university?.uni_code || "",
  };
  // Where the user originally came from (external referrer).
  // Sent as its own key — does NOT touch the ERP `c1` column.
  // Empty for direct visits; harmless to omit then.
  const entryReferrer = getEntryReferrer();
  if (entryReferrer) o.lead_source_key = entryReferrer;
  /*
   * Certification profile, asked on the single step that replaced step 2.
   *
   * Written only when present, so every other lead form — none of which collect
   * these — sends exactly the payload it sent before.
   */
  if (formValues.experience) o.experience = formValues.experience;
  if (formValues.job_domain) o.job_domain = formValues.job_domain;
  if (formValues.c2) o.c2 = formValues.c2;
  if (formValues.c3) o.c3 = formValues.c3;
  if (formValues.c4) o.c4 = formValues.c4;
  if (formValues.c5) o.c5 = formValues.c5;
  return o;
}

/** Thrown when email fails validation; forms should set `errors.email` — do not toast. */
export class LeadEmailValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LeadEmailValidationError";
    Object.setPrototypeOf(this, LeadEmailValidationError.prototype);
  }
}

export type FormValues = {
  phone: string;
  name: string;
  email: string;
  qualification?: string;
  course?: string;
  interest?: string;
  highest_qualification?: string;
  media: string;
  campaign: string;
  otp?: string;
  c2?: string;
  c3?: string;
  c4?: string;
  c5?: string;
  /**
   * Per-lead overrides for the CRM sub-entity and source.
   *
   * Both normally come from the university row, which is right for almost every
   * lead. A caller sets these when a specific lead must be routed somewhere
   * else — lead form 2001 uses them to send certification enquiries to their own
   * sub-entity. Omitted by every other caller, so behaviour is unchanged.
   */
  uni_code?: string;
  source_source?: string;
  /**
   * Banded years of experience and job function, collected on step 1 of the
   * certification flow. Optional — every other caller omits them.
   */
  experience?: string;
  job_domain?: string;
};

export const useCreateLead = () => {
  const dispatch = useDispatch();

  const { toast } = useToast();

  const { university } = useSelector((state: RootState) => state.globalslice);

  const [isExistingLead, setIsExistingLead] = useState<boolean>(false);
  const [leadsId, setLeadsId] = useState<string | null>(null);
  const [isExistingLeadId, setIsExistingLeadId] = useState<string>("");


  const [showOtp, setShowOtp] = useState(false);
  const isPreview = useSelector((state: RootState) => state.leaddrop.isPreview);

  const createNewLead = async (formValues: FormValues, phoneNumber: string) => {
    const leadData: LeadDataProps = {
      ...formValues,
      phone: phoneNumber,
      refURL: window.location.href,
      source: university?.source || "",
      uni_code: university?.uni_code || "",
      qualification:
        formValues.qualification || formValues.highest_qualification || "",

      interest: formValues.course || formValues.interest || "",
    };

    const createLeadRes = await createLead(leadData, isExistingLeadId);

    if (createLeadRes?.data) {
      setIsExistingLeadId(createLeadRes?.data?.leadId || "");
      setLeadsId(createLeadRes?.data?.lead?.leads_id || "");
      setShowOtp(true);

      return createLeadRes;
    } else {
      toast({
        variant: "destructive",

        title: "Uh oh! Something went wrong.",

        description: createLeadRes?.message || "Something went wrong",
      });

      throw new Error(createLeadRes?.message || "Failed to create lead");
    }
  };

  const handleOtpProcess = async (formValues: FormValues) => {
    const getLeadData = await lookupLeadAndSendOtp(formValues.phone);

    if (getLeadData?.data?.lead) {
      sessionStorage.setItem(
        "u_details",
        JSON.stringify(getLeadData?.data?.lead)
      );
    }

    if (!getLeadData?.data?.leadExists) {
      const emailError = getLeadEmailFieldError(formValues.email || "");
      if (emailError) {
        throw new LeadEmailValidationError(emailError);
      }
      setIsExistingLead(false);
      sessionStorage.removeItem("u_details");
      await createNewLead(formValues, formValues.phone);
    } else {
      if (formValues.name.length !== 0) {
        const emailError = getLeadEmailFieldError(formValues.email || "");
        if (emailError) {
          throw new LeadEmailValidationError(emailError);
        }
        const updatedFormValue: LeadDataProps = {
          ...formValues,
          phone: formValues.phone,
          refURL: window.location.href,
          source: university?.source || "",
          qualification:
            formValues.qualification || formValues.highest_qualification || "",
          interest: formValues.course || formValues.interest || "",
        };
        const leadId = getLeadData?.data?.lead?.id;
        if (leadId) {
          await updateLead(updatedFormValue, leadId, "");
        }
      }

      setIsExistingLead(true);
      setIsExistingLeadId(getLeadData?.data?.lead?.id || "");
      setLeadsId(getLeadData?.data?.lead?.leads_id || "");
    }

    dispatch(
      setLeadDetailsAction({ isExistingLead: !!getLeadData?.data?.leadExists })
    );
    setShowOtp(true);
    return getLeadData;
  };

  const verifyOtpAndSubmit = async (formValues: FormValues, otp: string) => {
    if (!otp) {
      throw new Error("Please enter OTP");
    }

    // Existing leads never enter an email (the field is not shown for them),
    // so only validate email for new users.
    if (!isExistingLeadId) {
      const emailError = getLeadEmailFieldError(formValues.email || "");
      if (emailError) {
        throw new LeadEmailValidationError(emailError);
      }
    }

    if (isExistingLeadId) {
      const leadData: LeadDataProps = {
        ...formValues,
        phone: formValues.phone,
        refURL: window.location.href,
        source: university?.source || "",
        qualification:
          formValues.qualification || formValues.highest_qualification || "",
        interest: formValues.course || formValues.interest || "",
      };

      if (!isExistingLeadId) {
        await updateLead(leadData, isExistingLeadId, "");
      }

      // Before verify: the server drops to the CRM off the stored row in the same request.
      await updateLeadUrlAttribution(isExistingLeadId);
      const response = await verifyOtp(isExistingLeadId, otp, isPreview);

      if (response?.data?.verified) {
        // Server-authoritative first-vs-returning (atomic on the is_verified flip):
        //   firstVerification === true  → this OTP verified the mobile for the FIRST
        //                                 time → last screens show their Thank-You state.
        //   firstVerification === false → the number was verified before (returning
        //                                 lead) → Welcome-Back state.
        // `leadExists` can't tell these apart (useNonVerifiedCapture creates a row on
        // the first valid keystroke), so the flag overrides the pre-OTP heuristic.
        // Absent/null (older server / DB failure) → keep the previous behaviour.
        // `firstVerification` may arrive as a boolean OR a string ("1"/"0"/"true"/
        // "false"); a raw `typeof === "boolean"` guard drops the string form, leaving a
        // first-timer flagged existing (isExistingLead stays true from the capture-row
        // leadExists) → wrong "Welcome Back". Coerce robustly; null = server sent nothing
        // usable → keep the prior default (treat as existing).
        const firstRaw = response?.data?.firstVerification;
        const first =
          firstRaw === undefined || firstRaw === null
            ? null
            : isCmsShowFlagOn(firstRaw, false);
        if (first !== null) {
          setIsExistingLead(!first);
        }
        dispatch(
          setLeadDetailsAction({
            isExistingLead: first !== null ? !first : true,
          })
        );
        dispatch(setIsLeadSubmittedSuccessfully(true));
        sessionStorage.setItem("isUserVerified", "true");
        return response;
      } else {
        return false;
      }
    } else {
      throw new Error("Something went wrong");
    }
  };

  /** IIT-KGP flow: send OTP with `{ data: { mobile } }` only. */
  const sendOtpMobileFlow = async (phone: string) => {
    const res = await sendOtpMobile(phone);
    if (!res?.success || res?.data?.leadId == null) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description:
          (res as { message?: string })?.message || "Could not send OTP",
      });
      throw new Error((res as { message?: string })?.message || "sendOtpMobile failed");
    }
    setIsExistingLeadId(String(res.data.leadId));
    setLeadsId("");
    dispatch(setLeadDetailsAction({ isExistingLead: false }));
    return res;
  };

  /** IIT-KGP: verify OTP → `isExistingUser` + token/redirect if existing; else `needsProfileCompletion`. */
  const submitAfterOtpFlow = async (
    formValues: FormValues,
    otpDigits: string,
    options: {
      includeRegistrationData: boolean;
      /**
       * The page's linked programme, sent read-only as `page_area_of_interest`
       * so the server can compare it against the stored one without writing it.
       * Callers that pass this own the mismatch flow (re-show the registration
       * form; the ERP drop happens on that submit). Omit to keep the legacy
       * behaviour where the page's programme is written through as
       * `area_of_interest`.
       */
      pageProgramme?: string | null;
    }
  ) => {
    if (!otpDigits || otpDigits.length !== 4) {
      throw new Error("Please enter OTP");
    }
    if (!isExistingLeadId) {
      throw new Error("Something went wrong");
    }
    if (options.includeRegistrationData) {
      const emailError = getLeadEmailFieldError(formValues.email || "");
      if (emailError) {
        throw new LeadEmailValidationError(emailError);
      }
    }

    // Mismatch-aware callers send the page's programme read-only: writing it
    // through as `area_of_interest` would overwrite the stored programme before
    // anything could notice the two differ, which is exactly the signal used to
    // re-show the registration form. The server drops to ERP off the stored row,
    // so the programme no longer has to be written here to reach ERP.
    const pageProgramme = String(options.pageProgramme ?? "").trim();
    const mismatchAware = options.pageProgramme !== undefined;

    // Legacy (non-mismatch-aware) callers keep carrying the programme in context
    // so an existing profile-complete lead still drops for THAT programme without
    // needing cookies from a previous browser/session.
    const hasProgrammeContext = Boolean(
      String(formValues.course || formValues.interest || "").trim()
    );
    const regPayload = options.includeRegistrationData
      ? buildMobileOtpLeadPayload(formValues, university)
      : mismatchAware
        ? pageProgramme
          ? { page_area_of_interest: pageProgramme }
          : undefined
        : hasProgrammeContext
          ? buildMobileOtpLeadPayload(formValues, university)
          : undefined;

    // Before submit: the server drops to the CRM off the stored row in the same request.
    await updateLeadUrlAttribution(isExistingLeadId);
    const raw = await submitAfterOtp(
      isExistingLeadId,
      otpDigits,
      isPreview,
      regPayload
    );

    if (
      raw &&
      typeof raw === "object" &&
      "success" in raw &&
      raw.success &&
      "data" in raw &&
      raw.data &&
      typeof raw.data === "object"
    ) {
      const d = raw.data as Record<string, unknown>;
      if (
        d.verified === true &&
        d.isExistingUser === false &&
        d.needsProfileCompletion === true
      ) {
        return { needsProfileCompletion: true as const };
      }
    }

    if (
      raw &&
      typeof raw === "object" &&
      "success" in raw &&
      raw.success &&
      "data" in raw &&
      raw.data &&
      typeof raw.data === "object" &&
      "verified" in raw.data &&
      raw.data.verified === false
    ) {
      return raw as { success: true; data: { verified: false; message?: string } };
    }

    if (
      raw &&
      typeof raw === "object" &&
      "success" in raw &&
      raw.success &&
      "data" in raw &&
      raw.data &&
      typeof raw.data === "object" &&
      "verified" in raw.data &&
      (raw.data as Record<string, unknown>).verified === true &&
      (raw.data as Record<string, unknown>).isExistingUser === true
    ) {
      return raw;
    }

    /** New-user profile submit after OTP: verified without further profile step */
    if (
      options.includeRegistrationData &&
      raw &&
      typeof raw === "object" &&
      "success" in raw &&
      raw.success &&
      "data" in raw &&
      raw.data &&
      typeof raw.data === "object" &&
      "verified" in raw.data &&
      (raw.data as Record<string, unknown>).verified === true &&
      (raw.data as Record<string, unknown>).needsProfileCompletion !== true
    ) {
      return raw as { success: true; data: Record<string, unknown> };
    }

    return false;
  };

  return {
    createNewLead,
    handleOtpProcess,
    verifyOtpAndSubmit,
    sendOtpMobileFlow,
    submitAfterOtpFlow,
    isExistingLead,
    isExistingLeadId,
    showOtp,
    setShowOtp,
    setIsExistingLeadId,
    setIsExistingLead,
    leadsId,
    setLeadsId
  };
};

export const useUpdateLeadLastScreenData = () => {
  const handleUpdateLeadLastScreenData = async (
    formValues: {
      location: string;
      jobTitle: string;
      industry: string;
      experience: string;
    },

    isExistingLeadId: string,

    isLeadFormExtraFields: string
  ) => {
    const response = await updateLead(
      formValues as unknown as LeadDataProps,
      isExistingLeadId,
      isLeadFormExtraFields
    );

    if (response?.data) {
      return response;
    } else {
      throw new Error("Something went wrong");
    }
  };

  return {
    handleUpdateLeadLastScreenData,
  };
};
